import React, { Fragment, useState, useEffect } from "react";
import ModelOption from "components/ModelOption";
import {
  BUTTON_CLASS_CONTEXT,
  LIST_PRESENTATION,
  PRESENTATION_LOAD_COUNT
} from "utils/constants/ui";
import SlimCTA from "components/SlimCTA";
import Button from "components/Form/fields/Button";
import {
  ModelSearchContextProvider,
  useModelSearchContext
} from "utils/context/modelSearch";
import IndexActionBar from "components/IndexActionBar";
import ListViewWrapper from "components/ListViewWrapper";
import GridViewWrapper from "components/GridViewWrapper";
import { useDefaultGridView } from "utils/hooks/presentation";
import { prepareOptionsList } from "utils/options";
import PresentLayout from "components/PresentLayout";
import startCase from "lodash/startCase";
import { matchSorter } from "match-sorter";

const DEFAULT_CLASSES = {
  container: "",
  inputWrapper: "flex flex-row items-center w-100 pr2 pl2-ns",
  input:
    "f5 f6-ns border-box input-reset ba pa2 db w-100 br2 lh-copy b--black-20",
  item: "",
  option: {},
  list: {}
};

const SLIM_CTA_CLASSES = {
  container:
    "w-100 flex flex-column flex-row-ns justify-between pa3 items-start items-center-ns bg-white",
  subheader: "f6"
};

const GridView = ({ models, loadCount, ModelComponent, customClasses }) => (
  <GridViewWrapper
    models={models}
    loadCount={loadCount}
    ModelComponent={ModelComponent}
    customClasses={customClasses}
  />
);

const ListView = ({ models, loadCount, ModelComponent, customClasses }) => (
  <ListViewWrapper
    models={models}
    loadCount={loadCount}
    ModelComponent={ModelComponent}
    customClasses={customClasses}
  />
);

const LIST_COMPONENTS = {
  [LIST_PRESENTATION.LIST]: ListView,
  [LIST_PRESENTATION.GRID]: GridView
};

const ModelSearch = ({
  activeModelIds = [],
  barActions = [],
  emptyComponent,
  onClick = () => {},
  customClasses,
  query,
  components,
  models,
  insert,
  initalPresentation,
  placeholder
}) => {
  const hasMultiplePresentations = Object.keys(components).length > 1;
  const modelName = startCase(query.model).toLowerCase();
  const { values, setValues } = useModelSearchContext({
    presentation: initalPresentation
  });
  const { presentation, search } = values;
  const additionalClasses =
    customClasses && presentation ? customClasses[presentation] : {};
  const classes = { ...DEFAULT_CLASSES, ...additionalClasses };

  const OptionComponent =
    (components && components[presentation]) || ModelOption;
  const ListComponent = LIST_COMPONENTS[presentation];

  const presLoadCount = PRESENTATION_LOAD_COUNT[presentation];
  const [loadCount, setLoadCount] = useState(presLoadCount);
  useEffect(() => {
    if (presLoadCount > loadCount) {
      setLoadCount(presLoadCount);
    }
  }, [presentation]);

  useDefaultGridView({
    models,
    setValues,
    values
  });

  const filtered = search
    ? matchSorter(models, search, {
        keys: query.keys
      })
    : models;

  const noneMatchSearch = !filtered.length && search;
  let content;
  if (filtered.length) {
    const disableMore = loadCount >= filtered.length;
    const searchNote = search
      ? `${filtered.length} matches`
      : `${disableMore ? filtered.length : loadCount} of ${filtered.length}`;

    const preparedOptions = prepareOptionsList({
      models: filtered,
      onClick,
      loadCount,
      customClasses: classes.option,
      activeModelIds
    });

    content = (
      <Fragment>
        {insert}
        {ListComponent && (
          <ListComponent
            models={preparedOptions}
            loadCount={loadCount}
            ModelComponent={OptionComponent}
            customClasses={classes.list}
          />
        )}
        <div className="pv3 w-100 flex flex-row justify-between items-center">
          <div className="f6">{searchNote}</div>
          <Button
            theme="slim"
            copy="Load all"
            disabled={disableMore}
            classes={{
              contextClass: BUTTON_CLASS_CONTEXT.SECONDARY
            }}
            onClick={() => {
              setLoadCount(models.length);
            }}
          />
        </div>
      </Fragment>
    );
  } else if (noneMatchSearch) {
    content = (
      <SlimCTA
        subheader={`No ${modelName} matches your search.`}
        customClasses={SLIM_CTA_CLASSES}
        actions={[
          {
            copy: "Clear",
            onClick: () =>
              setValues({
                ...values,
                search: ""
              })
          }
        ]}
      />
    );
  } else {
    content = emptyComponent || (
      <SlimCTA
        customClasses={SLIM_CTA_CLASSES}
        subheader="No models available."
      />
    );
  }

  const header = (
    <div className="mb2">
      <IndexActionBar
        hidePresentationToggle={!hasMultiplePresentations}
        setValues={setValues}
        values={values}
        search={{
          placeholder: placeholder || `Search by ${modelName} name`
        }}
        actions={barActions}
      />
    </div>
  );
  const presentProps = {
    header,
    content
  };

  return <PresentLayout {...presentProps} />;
};

const ModelSearchList = (props) => (
  <ModelSearchContextProvider>
    <ModelSearch {...props} />
  </ModelSearchContextProvider>
);

export default ModelSearchList;
